import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useGameSlug } from '@pkg/hooks';
import { getNextPageParam } from '@pkg/hooks/deprecated';

import {
    type ApiResult,
    apiReturnPaginatedResults,
    type BaseParams,
    links,
    type PaginatedResult,
    type PagingParams,
    Request,
    type ResourceId,
    type TeamLeaderboardItem,
} from '@app/client';
import { TEAM_COMPETITION_WINNERS_CACHE } from '@app/client/resources/competitions';

type UseGetCompetitionLeaderboardProps = {
    slug?: string;
    disabled?: boolean;
    interval?: boolean;
};

type FantasyLeaderboardParams = BaseParams<PagingParams> & { slug: ResourceId };

export async function getLeaderBoardTeamCompetitionWishlists(
    params: FantasyLeaderboardParams,
): Promise<ApiResult<PaginatedResult<TeamLeaderboardItem[]>>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.slug).teamLeaderBoard(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}

export const callLeaderboardTeamCompetitionWishlist = (params: FantasyLeaderboardParams) => {
    return apiReturnPaginatedResults<TeamLeaderboardItem>(getLeaderBoardTeamCompetitionWishlists, {
        slug: params.slug,
        settings: params.settings,
    });
};

export const useGetCompetitionTeamLeaderboard = (props?: UseGetCompetitionLeaderboardProps) => {
    const { disabled, slug: providedSlug } = props || {};

    const gameSlug = useGameSlug();
    const slug = providedSlug || gameSlug;
    const cache = [TEAM_COMPETITION_WINNERS_CACHE, slug];

    return useInfiniteQuery({
        queryKey: cache,
        queryFn: ({ signal, pageParam }) =>
            callLeaderboardTeamCompetitionWishlist({
                slug,
                settings: { signal },
                query: {
                    page: pageParam || 0,
                },
            }),

        refetchInterval: props?.interval ? 5000 : undefined,
        enabled: Boolean(slug) && !disabled,
        initialPageParam: 0,
        getNextPageParam,
        placeholderData: keepPreviousData,
        retry: false,
        notifyOnChangeProps: 'all',
    });
};
